<script setup>
/**
 * A month's chapter (journal practice 2026-10-28, DESIGN §6; API.md §6):
 * written once on the first night of the next month, then the seeker's to
 * edit like an entry. Three short sections made of their own lines plus the
 * thread's name: the hunches they started with, what changed ("From … to …"),
 * what they're still sensing. The one edit pattern (§7b): tap it (or Edit) and
 * the whole chapter becomes one field in place; once edited their text is the
 * chapter, with a way back to the written one.
 */
import { ref, computed } from 'vue'
import JournalInPlace from './JournalInPlace.vue'
import { shortDate } from '@/utils/journal-book'
import { chapterPlainText } from '@/utils/journal-practice'

const props = defineProps({
  chapter: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  backLabel: { type: String, default: '' },
})
const emit = defineEmits(['back', 'save'])

const field = ref(null)
const plain = computed(() => chapterPlainText(props.chapter))
const mine = computed(() => !!(props.chapter && props.chapter.edited && props.chapter.text))
const paras = computed(() => (mine.value ? props.chapter.text.split(/\n+/).map((p) => p.trim()).filter(Boolean) : []))
function editIt () { if (field.value && field.value.start) field.value.start() }
</script>

<template>
  <div class="chv" data-testid="journal-chapter">
    <div class="krow"><span></span><button type="button" class="bk" data-testid="journal-view-back" @click="emit('back')">‹ {{ backLabel || 'Back' }}</button></div>
    <p v-if="loading && !chapter" class="hidnote">Opening the chapter…</p>
    <template v-if="chapter">
      <div class="chh">
        <div class="k">A chapter</div>
        <h2>{{ chapter.title }}</h2>
        <p>
          {{ mine ? 'Edited by you' : 'Written when the month closed' }} · <button type="button" class="lnk" data-testid="journal-chapter-edit" @click="editIt">Edit</button>
          <template v-if="mine"> · <button type="button" class="lnk" data-testid="journal-chapter-reset" @click="emit('save', '')">Back to the written chapter</button></template>
        </p>
      </div>
      <JournalInPlace ref="field" tag="div" size="block" :text="plain" :one-line="false" note="Your chapter. The overnight writer never rewrites it."
                      label="Change this chapter" testid="journal-chapter-text" @save="emit('save', $event)">
        <div v-if="mine" class="mine"><p v-for="(p, i) in paras" :key="i">{{ p }}</p></div>
        <template v-else>
          <div v-for="s in chapter.sections" :key="s.id" class="chs">
            <h3>{{ s.title }}</h3>
            <ul>
              <li v-for="(l, li) in s.lines" :key="li"><span v-if="l.thread" class="tn">{{ l.thread }}</span>{{ l.text }}<em v-if="l.key">{{ shortDate(l.key) }}</em></li>
            </ul>
          </div>
        </template>
      </JournalInPlace>
    </template>
  </div>
</template>

<style scoped>
.chv { color: var(--j-ink); }
.krow { display: flex; align-items: center; justify-content: space-between; min-height: 34px; padding: 16px 30px 0 0; }
.bk { border: 0; background: none; padding: 8px 0; cursor: pointer; font: 600 13px var(--j-ui); color: var(--j-ink2); }
.chh { text-align: center; padding: 6px 0 8px; }
.chh .k { font: 600 10px var(--j-display); letter-spacing: .24em; text-transform: uppercase; color: var(--j-hand); }
.chh h2 { margin: 6px 0 0; font: 600 44px/1 var(--j-serif); color: var(--j-ink); }
.chh p { margin: 6px 0 0; font: italic 500 16px var(--j-serif); color: var(--j-ink3); }
.lnk { border: 0; background: none; padding: 0; cursor: pointer; font: italic 500 16px var(--j-serif); color: var(--j-ink3); }
.lnk:hover { color: var(--j-ink2); text-decoration: underline; text-underline-offset: 3px; }
.chs { margin: 22px 0 0; }
.chs h3 { margin: 0 0 6px; font: 600 10.5px var(--j-display); letter-spacing: .2em; text-transform: uppercase; color: var(--j-hand); }
.chs ul { margin: 0; padding: 0; list-style: none; }
.chs li { padding: 4px 0; font: 500 18px/1.4 var(--j-serif); color: var(--j-ink); }
.chs li em { margin-left: 5px; color: var(--j-ink3); font-size: 15px; white-space: nowrap; }
.chs li .tn { font: 600 13px var(--j-ui); color: var(--j-hand); margin-right: 6px; }
.mine { margin: 18px 0 0; }
.mine p { margin: 0 0 10px; font: 500 18px/1.45 var(--j-serif); color: var(--j-ink); }
.hidnote { margin: 14px 0 0; font: italic 500 15px var(--j-serif); color: var(--j-ink3); }
@media (max-width: 899px) { .chh h2 { font-size: 38px; } }
</style>
